import { useState, useCallback, useEffect, useRef } from 'react';
import { stringify } from 'query-string';
import { useThemeObserver } from '@atlaskit/tokens';
import { ABORT_ERR } from '../../constants';
import type { Props, Provider } from './types';

/**
 * PaginationProvider maintains pagination state
 * onQuery change this provider verifies whether any data present in state
 * if relevant data exist in state this hook will pass down page Data to child components
 * if data doesn't exist it will make a fetch request to pull down relevant values and then update state
 * pageData - holds current page data
 * pageCache - maintains state/cache
 */
const usePaginationProvider = <T,>({
	baseUrl,
	projectKey,
	onFetch,
	cache,
	onError,
	onTransformSource,

	// In case the resource is not included in SSR, this should be true so that the ssr paint includes the spinner
	initialLoadingState = false,
}: Props<T>): Provider<T> => {
	const { colorMode } = useThemeObserver();
	const [loading, setLoading] = useState(initialLoadingState);
	const [error, setError] = useState<unknown>();
	// current page data
	const [pageData, setPageData] = useState<unknown>();

	// source/collection of all previous pages data
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [pageCache, updatePageCache] = useState<any>(cache || new Map());

	// ref to abort stale fetch request's
	const abortController = useRef<unknown>(null);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const getCacheKey = (request: any): string => {
		const queryString = stringify({ ...request });
		return queryString;
	};

	const transformPageData = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(response: any) => {
			const transformedPageData = onTransformSource ? onTransformSource(response) : response;
			setPageData(transformedPageData);
			setLoading(false);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[onTransformSource, colorMode],
	);

	const getPageDataFromCache = useCallback((key: string) => pageCache.get(key), [pageCache]);

	// compare state and fetch / update if necessary
	const compareStateAndFetch = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		async (request: any) => {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			if (abortController.current?.abort) {
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				abortController.current.abort();
			}

			if (!request) {
				setLoading(false);
				return;
			}
			setLoading(true);
			setError(undefined);

			const queryKey = getCacheKey(request);
			const cachedData = getPageDataFromCache(queryKey);

			if (cachedData) {
				transformPageData(cachedData);
			} else {
				try {
					abortController.current = new AbortController();
					const response = await onFetch({
						...request,
						baseUrl,
						projectKey,
						// @ts-expect-error - TS2571 - Object is of type 'unknown'.
						signal: abortController.current.signal,
					});
					// @ts-expect-error - TS7006 - Parameter 'prev' implicitly has an 'any' type.
					updatePageCache((prev) => prev.set(queryKey, response));
					transformPageData(response);
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (err: any) {
					// The previous request is aborted. We don't need to send error.
					if (err?.code === ABORT_ERR) return;

					// To prevent error logging for users running very old versions of Chrome
					const isAbortControllerReferenceError =
						err?.name === 'ReferenceError' && err?.message?.includes('AbortController');

					if (!isAbortControllerReferenceError) onError(err);

					setError(err.message);
					setPageData(undefined);
					setLoading(false);
				}
			}
		},
		[baseUrl, getPageDataFromCache, onError, onFetch, projectKey, transformPageData],
	);

	// on user action - query/page-number change
	const fetch = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		async (requestQuery: any) => {
			compareStateAndFetch(requestQuery);
		},
		[compareStateAndFetch],
	);

	const clearCache = useCallback(() => {
		// @ts-expect-error - TS7006 - Parameter 'prev' implicitly has an 'any' type.
		updatePageCache((prev) => {
			prev.clear();
			return prev;
		});
	}, []);

	const resetProvider = useCallback(() => {
		setLoading(true);
		setError(undefined);
		setPageData(undefined);
		clearCache();
	}, [clearCache]);

	// clear Cache on un-mount
	useEffect(
		() => () => {
			clearCache();
		},
		[clearCache, updatePageCache],
	);

	return {
		loading,
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string | undefined'.
		error,
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'T | undefined'.
		data: pageData,
		fetch,
		resetProvider,
	};
};

export default usePaginationProvider;
